<div class="detail-container" *ngIf="!isLoading && !errorMessage">

  <div id="trip-map" class="trip-map"></div>

  <!-- Walk + Edit Controls -->
  <div class="walk-controls" *ngIf="catchFeatures.length > 0">
    <div class="walk-nav">
      <button mat-stroked-button (click)="selectCatch(currentCatchIndex - 1)" [disabled]="currentCatchIndex === 0 || isEditMode">← Prev</button>
      <span class="walk-label">
        {{ catchFeatures[currentCatchIndex].properties?.['fish_type_full_name'] ?? 'Unknown' }}
        &nbsp;({{ currentCatchIndex + 1 }} / {{ catchFeatures.length }})
      </span>
      <button mat-stroked-button (click)="selectCatch(currentCatchIndex + 1)" [disabled]="currentCatchIndex === catchFeatures.length - 1 || isEditMode">Next →</button>
    </div>
    <div class="walk-edit" *ngIf="!isEditMode">
      <button mat-stroked-button class="edit-loc-btn" (click)="startEdit()">Edit Location</button>
      <a mat-stroked-button class="view-catch-btn" [routerLink]="['/catch', catchFeatures[currentCatchIndex].id]">View Catch</a>
    </div>
    <div class="walk-edit" *ngIf="isEditMode">
      <button mat-stroked-button class="save-loc-btn" (click)="saveLocation()" [disabled]="isSavingLocation">
        {{ isSavingLocation ? 'Saving…' : 'Save' }}
      </button>
      <button mat-stroked-button class="cancel-loc-btn" (click)="cancelEdit()" [disabled]="isSavingLocation">Cancel</button>
      <span class="edit-hint">Drag the orange pin to the correct location</span>
    </div>
  </div>

  <header class="trip-header">
    <app-back-link link="/trips" label="Back to Trips"></app-back-link>
    <div class="trip-name-row">
      <ng-container *ngIf="!editingName">
        <h2 class="trip-title">{{ tripName || ('Trip #' + tripId) }}</h2>
        <button class="edit-btn" (click)="startEditName()" title="Edit name">✎</button>
      </ng-container>
      <ng-container *ngIf="editingName">
        <input class="edit-input edit-input--title" [(ngModel)]="nameValue" placeholder="Enter trip name" />
        <button class="save-btn" (click)="saveName()" [disabled]="isSavingName">{{ isSavingName ? 'Saving…' : 'Save' }}</button>
        <button class="cancel-btn" (click)="cancelEditName()" [disabled]="isSavingName">Cancel</button>
      </ng-container>
    </div>
    <dl class="trip-meta" *ngIf="report">
      <dt>Started</dt>
      <dd>{{ report.start_time ? (report.start_time | date:'full') : '—' }}</dd>

      <dt>Ended</dt>
      <dd>{{ report.end_time ? (report.end_time | date:'full') : '—' }}</dd>

      <dt>Report Generated</dt>
      <dd>{{ report.generated_at ? (report.generated_at | date:'medium') : '—' }}</dd>

      <dt>Duration</dt>
      <dd>{{ formatDuration(report.duration_minutes) }}</dd>

      <dt>Distance</dt>
      <dd>{{ report.distance_miles !== null ? (report.distance_miles | number:'1.1-1') + ' miles' : '—' }}</dd>

      <dt>Total Catches</dt>
      <dd>{{ report.total_catches ?? '—' }}</dd>
    </dl>
  </header>

  <!-- No report yet -->
  <section class="report-section" *ngIf="reportNotFound">
    <p class="stub-text">No report has been generated for this trip yet.</p>
    <button mat-stroked-button (click)="regenerate()" [disabled]="isRegenerating"
      title="Generate an AI fishing report using all catch data, conditions, spot analysis, and angler notes for this trip">
      {{ isRegenerating ? 'Generating…' : 'Generate Report' }}
    </button>
  </section>

  <!-- Catch Summary -->
  <section class="report-section" *ngIf="report && catchEntries.length > 0">
    <h3>Catch Summary</h3>
    <table class="catch-table">
      <thead>
        <tr><th>Fish</th><th>Count</th></tr>
      </thead>
      <tbody>
        <tr *ngFor="let entry of catchEntries">
          <td>
            <span class="fish-dot" [style.background-color]="entry.color"></span>
            {{ entry.display_name }}
          </td>
          <td>{{ entry.count }}</td>
        </tr>
        <tr class="total-row">
          <td>Total</td>
          <td>{{ totalCatches }}</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- Conditions Summary -->
  <section class="report-section" *ngIf="report && report.conditions_summary">
    <h3>Conditions</h3>
    <dl class="conditions-dl">
      <ng-container *ngIf="report.conditions_summary!.temperature.min !== null">
        <dt>Air Temperature</dt>
        <dd>{{ report.conditions_summary!.temperature.min }}°F – {{ report.conditions_summary!.temperature.max }}°F
            (avg {{ report.conditions_summary!.temperature.avg }}°F)</dd>
      </ng-container>

      <ng-container *ngIf="report.conditions_summary!.water_temperature.min !== null">
        <dt>Water Temperature</dt>
        <dd>{{ report.conditions_summary!.water_temperature.min }}°F – {{ report.conditions_summary!.water_temperature.max }}°F</dd>
      </ng-container>

      <ng-container *ngIf="report.conditions_summary!.wind_speed_most_common">
        <dt>Wind (most common)</dt>
        <dd>{{ report.conditions_summary!.wind_speed_most_common }} {{ report.conditions_summary!.wind_direction_most_common }}</dd>
      </ng-container>

      <ng-container *ngIf="report.conditions_summary!.tide_cycles?.length">
        <dt>Tide Cycles</dt>
        <dd>{{ report.conditions_summary!.tide_cycles.join(', ') }}</dd>
      </ng-container>

      <ng-container *ngIf="report.conditions_summary!.short_forecasts?.length">
        <dt>Weather</dt>
        <dd>{{ report.conditions_summary!.short_forecasts.join(', ') }}</dd>
      </ng-container>
    </dl>
  </section>

  <!-- Fishing Spots (dwell events + catch-only visits) -->
  <section class="report-section" *ngIf="dwellEvents.length > 0">
    <h3>Fishing Spots</h3>
    <table class="catch-table">
      <thead>
        <tr><th>Spot</th><th>Location</th><th>Duration</th><th>Pings</th></tr>
      </thead>
      <tbody>
        <tr *ngFor="let ev of dwellEvents" class="dwell-row" [routerLink]="['/spot', ev.spot?.id]"
            [class.catch-only-row]="ev.source === 'catch'">
          <td>{{ ev.spot?.name || ('Spot #' + ev.spot?.id) }}</td>
          <td class="stub-text">{{ ev.spot?.location || '' }}</td>
          <td>{{ ev.duration_minutes != null ? ((ev.duration_minutes | number:'1.0-0') + ' min') : '—' }}</td>
          <td>{{ ev.ping_count != null ? ev.ping_count : '—' }}</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- Detect Fishing Spots -->
  <section class="report-section">
    <div class="detect-spots-row">
      <button mat-stroked-button (click)="detectSpots()" [disabled]="isDetectingSpots"
        title="Cluster GPS location pings from this trip into fishing spots based on dwell time. Re-running clears and recomputes spots for this trip only.">
        {{ isDetectingSpots ? 'Queueing…' : (dwellEvents.length > 0 ? 'Re-detect Fishing Spots' : 'Detect Fishing Spots') }}
      </button>
      <span class="detect-status" *ngIf="detectSpotsStatus">{{ detectSpotsStatus }}</span>
    </div>
  </section>

  <!-- Angler Notes -->
  <section class="report-section">
    <h3>Angler Notes</h3>
    <app-resizable-textarea
      [(ngModel)]="userNotes"
      placeholder="Optional — overall trip observations: structure fished, bait used, tidal timing that worked, etc. Included in report generation."
      [minRows]="1">
    </app-resizable-textarea>
    <div class="notes-actions">
      <button mat-stroked-button (click)="saveNotes()" [disabled]="isSavingNotes">
        {{ isSavingNotes ? 'Saving…' : 'Save Notes' }}
      </button>
    </div>
  </section>

  <!-- Report Text -->
  <section class="report-section report-text-section" *ngIf="report">
    <h3>Fishing Report</h3>
    <div *ngIf="report.report_text" class="report-md" [innerHTML]="report.report_text | markdown"></div>
    <p *ngIf="!report.report_text" class="stub-text">
      No report available yet — click Regenerate to generate one.
    </p>
    <button mat-stroked-button (click)="regenerate()" [disabled]="isRegenerating" style="margin-top: 8px;"
      title="Re-generate the AI report, picking up any new catch analysis, updated spot data, or angler notes added since the last generation">
      {{ isRegenerating ? 'Regenerating…' : 'Regenerate Report' }}
    </button>
  </section>

  <!-- Complete Catch List -->
  <section class="report-section" *ngIf="catchFeatures.length > 0">
    <h3>All Catches</h3>
    <app-catch-table [features]="catchFeatures"></app-catch-table>
  </section>

</div>

<app-loading-spinner [isLoading]="isLoading"></app-loading-spinner>

<div class="error-panel" *ngIf="!isLoading && errorMessage">
  <p>{{ errorMessage }}</p>
  <a routerLink="/trips">← Return to Trips</a>
</div>
